import { memo, useMemo } from 'react';
import type { HeatmapData } from '../../hooks/useKeystrokeHeatmap';
import { KEY_FINGER, FINGER_CLASS, FINGERS } from '../../data/fingerMap';
import HandsSVG, { FINGER_CLASS_TO_ZONE } from './HandsSVG';

interface KeyboardHeatmapProps {
  heatmap: HeatmapData;
  /** The next character the user needs to type — drives finger highlight. Null when idle/complete. */
  nextKey: string | null;
  /** Whether to show finger zone labels on keys */
  showFingerLabels?: boolean;
}

// ── Layout ────────────────────────────────────────────────────────────────────

const ROWS: string[][] = [
  ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '='],
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', '[', ']', '\\'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ';', "'"],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '/'],
];

// ── Heat color ────────────────────────────────────────────────────────────────

/**
 * Maps a normalized intensity t ∈ [0, 1] to a smooth RGBA color.
 *
 * Color stops (perceptually even):
 *   0.00 → transparent (key at rest)
 *   0.20 → blue   rgba(74, 158, 255, 0.25)
 *   0.50 → green  rgba(64, 192, 87,  0.55)
 *   0.75 → yellow rgba(255, 200, 50, 0.70)
 *   1.00 → red    rgba(240, 82,  82, 0.85)
 */
function lerpRgba(
  [r1, g1, b1, a1]: [number, number, number, number],
  [r2, g2, b2, a2]: [number, number, number, number],
  t: number,
): string {
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  const a = +(a1 + (a2 - a1) * t).toFixed(3);
  return `rgba(${r},${g},${b},${a})`;
}

type Stop = [number, [number, number, number, number]];

const GRADIENT_STOPS: Stop[] = [
  [0.00, [74,  158, 255, 0.00]],
  [0.20, [74,  158, 255, 0.30]],
  [0.50, [64,  192, 87,  0.55]],
  [0.75, [255, 200, 50,  0.72]],
  [1.00, [240, 82,  82,  0.88]],
];

function heatColor(t: number): string | undefined {
  if (t <= 0) return undefined;
  for (let i = 1; i < GRADIENT_STOPS.length; i++) {
    const [t0, c0] = GRADIENT_STOPS[i - 1]!;
    const [t1, c1] = GRADIENT_STOPS[i]!;
    if (t <= t1) {
      const local = (t - t0) / (t1 - t0);
      return lerpRgba(c0, c1, local);
    }
  }
  return lerpRgba(GRADIENT_STOPS.at(-2)![1], GRADIENT_STOPS.at(-1)![1], 1);
}

function normalizedIntensity(count: number, max: number): number {
  if (max === 0 || count === 0) return 0;
  // sqrt scale: prevents dominant keys from washing out all others
  return Math.sqrt(count / max);
}

// ── Single key ────────────────────────────────────────────────────────────────

interface HeatKeyProps {
  keyId: string;
  label?: string;
  count: number;
  max: number;
  isNext: boolean;
  showFingerLabel: boolean;
  wide?: 'tab' | 'space' | 'enter' | 'bs';
}

const HeatKey = memo(function HeatKey({
  keyId,
  label,
  count,
  max,
  isNext,
  showFingerLabel,
  wide,
}: HeatKeyProps) {
  const t = normalizedIntensity(count, max);
  const bg = heatColor(t);
  const fingerId = KEY_FINGER[keyId];
  const fingerClass = fingerId !== undefined ? FINGER_CLASS[fingerId] : undefined;
  const fingerInfo = fingerId !== undefined ? FINGERS[fingerId] : undefined;

  const classes = [
    'heatmap-key',
    fingerClass,
    isNext ? 'heatmap-key--next' : '',
    wide ? `heatmap-key--${wide}` : '',
    t > 0 ? 'heatmap-key--warm' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      style={bg ? { '--key-heat': bg } as React.CSSProperties : undefined}
      title={
        fingerInfo
          ? `${label ?? keyId} — ${fingerInfo.name}${count > 0 ? ` (${count}×)` : ''}`
          : undefined
      }
    >
      <span className="heatmap-key-label">{label ?? keyId}</span>
      {showFingerLabel && fingerInfo && (
        <span className="heatmap-key-finger">{fingerInfo.short}</span>
      )}
      {count > 0 && (
        <span className="heatmap-key-count">{count >= 1000 ? `${(count / 1000).toFixed(1)}k` : count}</span>
      )}
    </div>
  );
});

// ── Legend ────────────────────────────────────────────────────────────────────

const FINGER_LEGEND = [
  { label: 'Pinky',  classes: ['f-l-pinky',  'f-r-pinky']  },
  { label: 'Ring',   classes: ['f-l-ring',   'f-r-ring']   },
  { label: 'Middle', classes: ['f-l-middle', 'f-r-middle'] },
  { label: 'Index',  classes: ['f-l-index',  'f-r-index']  },
  { label: 'Thumb',  classes: ['f-l-thumb',  'f-r-thumb']  },
] as const;

// ── Main component ────────────────────────────────────────────────────────────

const KeyboardHeatmap = memo(function KeyboardHeatmap({
  heatmap,
  nextKey,
  showFingerLabels = false,
}: KeyboardHeatmapProps) {
  const max = useMemo(() => {
    let m = 0;
    heatmap.forEach((v) => { if (v > m) m = v; });
    return m;
  }, [heatmap]);

  // Normalise the nextKey to lowercase for lookup (the map uses lowercase)
  const normalizedNext = nextKey ? nextKey.toLowerCase() : null;

  function renderKey(keyId: string, label?: string, wide?: HeatKeyProps['wide']) {
    return (
      <HeatKey
        key={keyId}
        keyId={keyId}
        label={label}
        count={heatmap.get(keyId) ?? 0}
        max={max}
        isNext={normalizedNext === keyId || (keyId === ' ' && normalizedNext === ' ')}
        showFingerLabel={showFingerLabels}
        wide={wide}
      />
    );
  }

  // Active finger info for the hint line + SVG highlight
  const nextFingerId   = nextKey ? KEY_FINGER[nextKey] ?? KEY_FINGER[nextKey.toLowerCase()] : undefined;
  const nextFingerInfo = nextFingerId !== undefined ? FINGERS[nextFingerId] : undefined;
  const nextFingerClass = nextFingerId !== undefined ? FINGER_CLASS[nextFingerId] : undefined;
  const activeZone = nextFingerClass ? FINGER_CLASS_TO_ZONE[nextFingerClass] : undefined;

  return (
    <div className="heatmap-wrapper">
      <div className="heatmap-header">
        <p className="heatmap-title">Keystroke Heatmap</p>
        {nextFingerInfo && (
          <p className="heatmap-finger-hint">
            Next: <span className={`heatmap-finger-name heatmap-finger-name--${nextFingerClass}`}>
              {nextFingerInfo.name}
            </span>
          </p>
        )}
      </div>

      {/* Hand guide SVG — overlays keyboard, pointer-events disabled inside */}
      <div className="heatmap-hands-container">
        <HandsSVG activeFinger={activeZone ?? null} className="heatmap-hands-svg" />
      </div>

      <div className="heatmap-keyboard">
        {ROWS.map((row, ri) => (
          <div key={ri} className={`heatmap-row heatmap-row-${ri}`}>
            {row.map((key) => renderKey(key))}
          </div>
        ))}
        <div className="heatmap-row heatmap-row-special">
          {renderKey('Tab',       'Tab',   'tab')}
          {renderKey(' ',         'Space', 'space')}
          {renderKey('Enter',     'Enter', 'enter')}
          {renderKey('Backspace', '⌫',     'bs')}
        </div>
      </div>

      <div className="heatmap-legend">
        {FINGER_LEGEND.map(({ label, classes }) => (
          <div key={label} className="heatmap-legend-item">
            <span className={`heatmap-legend-swatch ${classes[0]}`} />
            <span className="heatmap-legend-label">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

export default KeyboardHeatmap;
